// File: client/src/components/rules/expressionEditor/MonacoLanguageDefinitions.js

/**
 * Register custom language definitions for Monaco editor
 * @param {object} monaco - The Monaco editor instance
 */
export const registerEjsLanguages = (monaco) => {
    // Define special token colors for record object and properties
    monaco.editor.defineTheme('ejs-theme', {
        base: 'vs',
        inherit: true,
        rules: [
            { token: 'record', foreground: '0000ff', fontStyle: 'bold' },      // Blue bold for record
            { token: 'record.property', foreground: '0000ff' },                // Blue for record properties
            { token: 'workflow', foreground: '7c00ff', fontStyle: 'bold' },    // Purple bold for workflow
            { token: 'delimiter.ejs', foreground: '008000', fontStyle: 'bold' }  // Green bold for EJS delimiters
        ],
        colors: {}
    });

    // Tokenizers for HTML with EJS
    const createEjsHtmlTokenizer = () => ({
        defaultToken: '',
        tokenPostfix: '',

        // JavaScript keywords
        keywords: [
            'break', 'case', 'catch', 'class', 'continue', 'const',
            'constructor', 'debugger', 'default', 'delete', 'do', 'else',
            'export', 'extends', 'false', 'finally', 'for', 'from', 'function',
            'get', 'if', 'import', 'in', 'instanceof', 'let', 'new', 'null',
            'return', 'set', 'super', 'switch', 'symbol', 'this', 'throw', 'true',
            'try', 'typeof', 'undefined', 'var', 'void', 'while', 'with', 'yield',
            'async', 'await', 'of'
        ],

        // Our special data objects
        records: ['record', 'workflow'],

        // EJS opening and closing tags
        brackets: [
            { open: '<%', close: '%>', token: 'delimiter.ejs' },
            { open: '<%=', close: '%>', token: 'delimiter.ejs' },
            { open: '<%-', close: '%>', token: 'delimiter.ejs' },
        ],

        // The main tokenizer for HTML with EJS
        tokenizer: {
            root: [
                // EJS tags
                [/<%=/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%-/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%(?!\s*=|-|%)/, { token: 'delimiter.ejs', next: '@ejsCode' }],

                // Regular HTML
                [/<!DOCTYPE/, 'metatag', '@doctype'],
                [/<!--/, 'comment', '@comment'],
                [/(<)((?:[\w\-]+:)?[\w\-]+)(\s*)(\/>)/, ['delimiter', 'tag', '', 'delimiter']],
                [/(<)(script)/, ['delimiter', { token: 'tag', next: '@script' }]],
                [/(<)(style)/, ['delimiter', { token: 'tag', next: '@style' }]],
                [/(<)((?:[\w\-]+:)?[\w\-]+)/, ['delimiter', { token: 'tag', next: '@otherTag' }]],
                [/(<\/)((?:[\w\-]+:)?[\w\-]+)/, ['delimiter', { token: 'tag', next: '@otherTag' }]],
                [/</, 'delimiter'],
                [/[^<]+/, 'text'],
            ],

            // EJS expression - for <%= ... %> and <%- ... %>
            ejsExpression: [
                [/%>/, { token: 'delimiter.ejs', next: '@pop' }],
                // Special handling for record and workflow with nested properties
                [/\b(record)\b(?!\.)/, 'record'],
                [/\b(workflow)\b(?!\.)/, 'workflow'],
                [/\b(record)((?:\.[a-zA-Z0-9_]+)+)/, ['record', 'record.property']],
                [/\b(workflow)((?:\.[a-zA-Z0-9_]+)+)/, ['workflow', 'record.property']],
                // Regular JavaScript
                [/"([^"\\]|\\.)*$/, 'string.invalid'],
                [/'([^'\\]|\\.)*$/, 'string.invalid'],
                [/"/, 'string', '@string_double'],
                [/'/, 'string', '@string_single'],
                [/`/, 'string', '@string_backtick'],
                [/[0-9_]+\.[0-9_]+([eE][\-+]?\d+)?/, 'number.float'],
                [/[0-9_]+/, 'number'],
                [/[a-zA-Z_][\w$]*/, { cases: {
                        '@keywords': 'keyword',
                        '@records': 'record',
                        '@default': 'identifier'
                    }}],
                [/[\[\](){}<>=!+\-*/%&|?:.~^]/, 'delimiter'],
                [/[ \t\r\n]+/, 'white']
            ],

            // EJS code - for <% ... %> (scriptlets)
            ejsCode: [
                [/%>/, { token: 'delimiter.ejs', next: '@pop' }],
                [/\b(record)\b(?!\.)/, 'record'],
                [/\b(workflow)\b(?!\.)/, 'workflow'],
                [/\b(record)((?:\.[a-zA-Z0-9_]+)+)/, ['record', 'record.property']],
                [/\b(workflow)((?:\.[a-zA-Z0-9_]+)+)/, ['workflow', 'record.property']],
                [/"([^"\\]|\\.)*$/, 'string.invalid'],
                [/'([^'\\]|\\.)*$/, 'string.invalid'],
                [/"/, 'string', '@string_double'],
                [/'/, 'string', '@string_single'],
                [/`/, 'string', '@string_backtick'],
                [/[0-9_]+\.[0-9_]+([eE][\-+]?\d+)?/, 'number.float'],
                [/[0-9_]+/, 'number'],
                [/[a-zA-Z_][\w$]*/, { cases: {
                        '@keywords': 'keyword',
                        '@records': 'record',
                        '@default': 'identifier'
                    }}],
                [/[\[\](){}<>=!+\-*/%&|?:.~^]/, 'delimiter'],
                [/[ \t\r\n]+/, 'white']
            ],

            // HTML specific rules
            doctype: [
                [/[^>]+/, 'metatag.content'],
                [/>/, 'metatag', '@pop'],
            ],

            comment: [
                [/-->/, 'comment', '@pop'],
                [/[^-]+/, 'comment.content'],
                [/./, 'comment.content']
            ],

            otherTag: [
                [/\/?>/, 'delimiter', '@pop'],
                [/"([^"]*)"/, 'attribute.value'],
                [/'([^']*)'/, 'attribute.value'],
                [/[\w\-]+/, 'attribute.name'],
                [/=/, 'delimiter'],
                [/\s+/, '']
            ],

            // Script tag handling
            script: [
                [/type/, 'attribute.name'],
                [/=/, 'delimiter'],
                [/"([^"]*)"/, 'attribute.value'],
                [/'([^']*)'/, 'attribute.value'],
                [/>/, { token: 'delimiter', next: '@scriptContent' }],
                [/\s+/, ''],
                [/(<\/)(script\s*)(>)/, ['delimiter', 'tag', { token: 'delimiter', next: '@pop' }]]
            ],

            scriptContent: [
                [/<%=/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%-/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%/, { token: 'delimiter.ejs', next: '@ejsCode' }],
                [/<\/script\s*>/, { token: '@rematch', next: '@pop' }],
                [/./, 'text.javascript']
            ],

            // Style tag handling
            style: [
                [/type/, 'attribute.name'],
                [/=/, 'delimiter'],
                [/"([^"]*)"/, 'attribute.value'],
                [/'([^']*)'/, 'attribute.value'],
                [/>/, { token: 'delimiter', next: '@styleContent' }],
                [/\s+/, ''],
                [/(<\/)(style\s*)(>)/, ['delimiter', 'tag', { token: 'delimiter', next: '@pop' }]]
            ],

            styleContent: [
                [/<%=/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%-/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%/, { token: 'delimiter.ejs', next: '@ejsCode' }],
                [/<\/style\s*>/, { token: '@rematch', next: '@pop' }],
                [/./, 'text.css']
            ],

            // String rules
            string_double: [
                [/[^\\"]+/, 'string'],
                [/\\./, 'string.escape'],
                [/"/, 'string', '@pop']
            ],

            string_single: [
                [/[^\\']+/, 'string'],
                [/\\./, 'string.escape'],
                [/'/, 'string', '@pop']
            ],

            string_backtick: [
                [/\$\{/, { token: 'delimiter.bracket', next: '@bracketCounting' }],
                [/[^\\`$]+/, 'string'],
                [/\\./, 'string.escape'],
                [/`/, 'string', '@pop']
            ],

            bracketCounting: [
                [/\{/, 'delimiter.bracket', '@bracketCounting'],
                [/\}/, 'delimiter.bracket', '@pop'],
                { include: 'ejsExpression' }
            ]
        }
    });

    // Tokenizers for text with EJS
    const createEjsTextTokenizer = () => ({
        defaultToken: 'text',
        tokenPostfix: '',

        // JavaScript keywords
        keywords: [
            'break', 'case', 'catch', 'class', 'continue', 'const',
            'constructor', 'debugger', 'default', 'delete', 'do', 'else',
            'export', 'extends', 'false', 'finally', 'for', 'from', 'function',
            'get', 'if', 'import', 'in', 'instanceof', 'let', 'new', 'null',
            'return', 'set', 'super', 'switch', 'symbol', 'this', 'throw', 'true',
            'try', 'typeof', 'undefined', 'var', 'void', 'while', 'with', 'yield',
            'async', 'await', 'of'
        ],

        // Our special data objects
        records: ['record', 'workflow'],

        // EJS opening and closing tags
        brackets: [
            { open: '<%', close: '%>', token: 'delimiter.ejs' },
            { open: '<%=', close: '%>', token: 'delimiter.ejs' },
            { open: '<%-', close: '%>', token: 'delimiter.ejs' },
        ],

        // The main tokenizer for text with EJS
        tokenizer: {
            root: [
                // EJS tags
                [/<%=/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%-/, { token: 'delimiter.ejs', next: '@ejsExpression' }],
                [/<%(?!\s*=|-|%)/, { token: 'delimiter.ejs', next: '@ejsCode' }],
                [/./, 'text']
            ],

            // EJS expression - for <%= ... %> and <%- ... %>
            ejsExpression: [
                [/%>/, { token: 'delimiter.ejs', next: '@pop' }],
                [/\b(record)\b(?!\.)/, 'record'],
                [/\b(workflow)\b(?!\.)/, 'workflow'],
                [/\b(record)((?:\.[a-zA-Z0-9_]+)+)/, ['record', 'record.property']],
                [/\b(workflow)((?:\.[a-zA-Z0-9_]+)+)/, ['workflow', 'record.property']],
                [/"([^"\\]|\\.)*$/, 'string.invalid'],
                [/'([^'\\]|\\.)*$/, 'string.invalid'],
                [/"/, 'string', '@string_double'],
                [/'/, 'string', '@string_single'],
                [/`/, 'string', '@string_backtick'],
                [/[0-9_]+\.[0-9_]+([eE][\-+]?\d+)?/, 'number.float'],
                [/[0-9_]+/, 'number'],
                [/[a-zA-Z_][\w$]*/, { cases: {
                        '@keywords': 'keyword',
                        '@records': 'record',
                        '@default': 'identifier'
                    }}],
                [/[\[\](){}<>=!+\-*/%&|?:.~^]/, 'delimiter'],
                [/[ \t\r\n]+/, 'white']
            ],

            // EJS code - for <% ... %> (scriptlets)
            ejsCode: [
                [/%>/, { token: 'delimiter.ejs', next: '@pop' }],
                [/\b(record)\b(?!\.)/, 'record'],
                [/\b(workflow)\b(?!\.)/, 'workflow'],
                [/\b(record)((?:\.[a-zA-Z0-9_]+)+)/, ['record', 'record.property']],
                [/\b(workflow)((?:\.[a-zA-Z0-9_]+)+)/, ['workflow', 'record.property']],
                [/"([^"\\]|\\.)*$/, 'string.invalid'],
                [/'([^'\\]|\\.)*$/, 'string.invalid'],
                [/"/, 'string', '@string_double'],
                [/'/, 'string', '@string_single'],
                [/`/, 'string', '@string_backtick'],
                [/[0-9_]+\.[0-9_]+([eE][\-+]?\d+)?/, 'number.float'],
                [/[0-9_]+/, 'number'],
                [/[a-zA-Z_][\w$]*/, { cases: {
                        '@keywords': 'keyword',
                        '@records': 'record',
                        '@default': 'identifier'
                    }}],
                [/[\[\](){}<>=!+\-*/%&|?:.~^]/, 'delimiter'],
                [/[ \t\r\n]+/, 'white']
            ],

            // String rules
            string_double: [
                [/[^\\"]+/, 'string'],
                [/\\./, 'string.escape'],
                [/"/, 'string', '@pop']
            ],

            string_single: [
                [/[^\\']+/, 'string'],
                [/\\./, 'string.escape'],
                [/'/, 'string', '@pop']
            ],

            string_backtick: [
                [/\$\{/, { token: 'delimiter.bracket', next: '@bracketCounting' }],
                [/[^\\`$]+/, 'string'],
                [/\\./, 'string.escape'],
                [/`/, 'string', '@pop']
            ],

            bracketCounting: [
                [/\{/, 'delimiter.bracket', '@bracketCounting'],
                [/\}/, 'delimiter.bracket', '@pop'],
                { include: 'ejsExpression' }
            ]
        }
    });

    // Register HTML with EJS language
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'html-ejs')) {
        monaco.languages.register({ id: 'html-ejs' });
        monaco.languages.setMonarchTokensProvider('html-ejs', createEjsHtmlTokenizer());
    }

    // Register text with EJS language
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'text-ejs')) {
        monaco.languages.register({ id: 'text-ejs' });
        monaco.languages.setMonarchTokensProvider('text-ejs', createEjsTextTokenizer());
    }
};
